import { TruckDto } from '@trucktrax/trucktrax-ts-common';
import { EVICT_TRUCK_FROM_CACHE, UPDATE_TRUCK_ASSIGNMENT } from '../../constants/actionConstants';
import { PayloadAction } from '../../types';

/**
 * A driver was assigned to, or unassigned from, a truck (DEV-3840).
 *
 * Payload is the truck's post-change state: `driver` populated on assign,
 * absent on unassign. Dispatched from the messaging socket so the cached
 * truck list -- and the "Driver Name (TruckAlias)" labels the message center
 * derives from it -- stay current without a page refresh.
 *
 * Only `driver` is folded into a cached truck; the unassign payloads arrive
 * without their relations. The reducer owns that rule.
 */
export const updateTruckAssignment = (
  truck: TruckDto,
): PayloadAction<TruckDto, typeof UPDATE_TRUCK_ASSIGNMENT> => ({
  type: UPDATE_TRUCK_ASSIGNMENT,
  payload: truck,
});

/** The truck to drop, and the stamp of the event that moved it out of view. */
export type TruckEviction = { url: string; updatedTimestamp?: string };

/**
 * A cached truck left the dispatcher's view — its region or product line was
 * changed out from under them (DEV-3840).
 *
 * Dropping it is not cosmetic: the cached record still carries a driver
 * pairing, and `buildTruckMap` would keep labelling that driver with a truck
 * they are no longer reachable on.
 *
 * Carries the event's `updatedTimestamp` so the reducer can ignore an eviction
 * that is older than the cached record.
 */
export const evictTruckFromCache = (
  truck: TruckEviction,
): PayloadAction<TruckEviction, typeof EVICT_TRUCK_FROM_CACHE> => ({
  type: EVICT_TRUCK_FROM_CACHE,
  payload: { url: truck.url, updatedTimestamp: truck.updatedTimestamp },
});
